import React, { useState, useEffect ,useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";

import "react-quill/dist/quill.snow.css";
import slugify from "slugify";
import api from "../api/api";

export default function AdminBlogEditor() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("edit");

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [published, setPublished] = useState(false);

  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);

  // Auto-generate slug
  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    setSlug(slugify(value, { lower: true, strict: true }));
  };

  // Fetch blog list
  const fetchBlogs = async () => {
    setLoadingBlogs(true);
    try {
      const res = await api.get("/blog", { validateStatus: () => true });
      if (res.status === 200) {
        if (Array.isArray(res.data)) setBlogs(res.data);
        else if (res.data.blogs && Array.isArray(res.data.blogs)) setBlogs(res.data.blogs);
        else setBlogs([]);
      }
    } catch (err) {
      console.error("Failed to fetch blogs:", err);
      setMessage({ type: "error", text: "Failed to fetch blogs" });
    } finally {
      setLoadingBlogs(false);
    }
  };

  // Load blog for editing
  useEffect(() => {
    if (!editId) return;

    const loadBlog = async () => {
      try {
        const res = await api.get(`/blog/${editId}`, { validateStatus: () => true });
        if (res.status === 200) {
          const b = res.data;
          setTitle(b.title || "");
          setSlug(b.slug || "");
          setContent(b.content || "");
          setCoverImage(b.coverImage || "");
          setCategory(b.category || "");
          setTags(b.tags || "");
          setPublished(!!b.published);
        } else {
          setMessage({ type: "error", text: "Blog not found for editing" });
        }
      } catch (err) {
        console.error("Error loading blog:", err);
        setMessage({ type: "error", text: "Failed to load blog for editing" });
      }
    };

    loadBlog();
  }, [editId]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Auto-dismiss messages
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(t);
  }, [message]);

  // Upload image (cover or inline)
  const handleImageUpload = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await api.post("/blog/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        validateStatus: () => true,
      });
      if (res.status === 200) return res.data.imageUrl;
      if (res.status === 401) setMessage({ type: "error", text: "⚠️ Session expired. Please log in." });
      if (res.status === 403) setMessage({ type: "error", text: "⛔ Not allowed to upload." });
      return null;
    } catch (err) {
      console.error("Image upload failed:", err);
      setMessage({ type: "error", text: "Image upload failed" });
      return null;
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await handleImageUpload(file);
    if (url) setCoverImage(url);
  };

  // Quill toolbar config
  const modules = useMemo(() => ({
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      [{ header: [1, 2, 3, false] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
    handlers: {
      image: function () {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = async () => {
          const file = input.files[0];
          const url = await handleImageUpload(file);
          if (url) {
            const quill = this.quill;
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, "image", url);
          }
        };
        input.click();
      },
    },
  },
}), []);

  // Publish / update blog
  const handlePublish = async () => {
    if (!title.trim() || !content.trim()) {
      setMessage({ type: "error", text: "Title and content required" });
      return;
    }

    setLoading(true);
    try {
      const payload = { title, slug, content, coverImage, category, tags, published };
      const res = editId
        ? await api.put(`/blog/${editId}`, payload, { validateStatus: () => true })
        : await api.post("/blog", payload, { validateStatus: () => true });

      if (res.status >= 200 && res.status < 300) {
        setMessage({ type: "success", text: editId ? "✅ Blog updated!" : "✅ Blog published!" });
        setTitle("");
        setSlug("");
        setContent("");
        setCoverImage("");
        setCategory("");
        setTags("");
        setPublished(false);
        setPreview(false);
        fetchBlogs();
        if (editId) navigate("/admin/blogs");
      } else {
        setMessage({ type: "error", text: res.data?.message || "Action failed" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to save blog" });
    } finally {
      setLoading(false);
    }
  };

  // Delete blog
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;
    try {
      const res = await api.delete(`/blog/${id}`, { validateStatus: () => true });
      if (res.status === 200) {
        setMessage({ type: "success", text: "🗑️ Blog deleted" });
        fetchBlogs();
      } else {
        setMessage({ type: "error", text: res.data?.message || "Delete failed" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Delete failed" });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded shadow">
      <h1 className="text-3xl font-bold mb-4">{editId ? "✏️ Edit Blog" : "📝 Create Blog"}</h1>

      {message && (
        <div
          className={`p-3 mb-4 rounded font-medium text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800 border border-green-300"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Blog Form */}
      <input
        type="text"
        value={title}
        onChange={handleTitleChange}
        placeholder="Enter blog title"
        className="w-full p-2 border rounded mb-3"
      />
      <input
        type="text"
        value={slug}
        readOnly
        className="w-full p-2 border rounded mb-3 bg-gray-100 text-gray-600"
      />

      {/* Cover Image */}
      <div className="mb-3">
        <label className="block font-medium mb-1">Cover Image</label>
        <input type="file" accept="image/*" onChange={handleCoverUpload} />
        {coverImage && (
          <img src={coverImage} alt="cover" className="mt-2 w-48 rounded shadow" />
        )}
      </div>

      <input
        type="text"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="Category"
        className="w-full p-2 border rounded mb-3"
      />

      <input
        type="text"
        value={tags}
        onChange={(e) => setTags(e.target.value)}
        placeholder="Tags (comma separated)"
        className="w-full p-2 border rounded mb-3"
      />

      <label className="flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={published}
          onChange={(e) => setPublished(e.target.checked)}
        />
        Publish immediately
      </label>
<div className="mb-4">
  <label className="block font-medium mb-1">Content</label>
  <ReactQuill
    theme="snow"
    value={content || ""}
    onChange={setContent}
    modules={modules}
    formats={[
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "link",
      "image",
    ]}
    style={{ minHeight: "300px", background: "#fff" }}
    className="border rounded"
  />
</div>


      <div className="flex justify-between mb-4">
        <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={() => setPreview(!preview)}>
          {preview ? "Hide Preview" : "Preview"}
        </button>
        <button
          type="button"
          onClick={handlePublish}
          disabled={!title.trim() || !content.trim() || loading}
          className="px-4 py-2 bg-indigo-600 text-white rounded"
        >
          {loading ? "Saving..." : editId ? "Update" : "Publish"}
        </button>
      </div>

      {preview && (
        <div className="border p-4 rounded bg-gray-50 mt-4">
          <h2 className="text-2xl font-semibold mb-2">{title}</h2>
          {coverImage && <img src={coverImage} alt="preview" className="mb-3 w-64 rounded shadow" />}
          <div dangerouslySetInnerHTML={{ __html: content }} />
          <p className="mt-2 text-sm text-gray-600">
            Category: {category} | Tags: {tags} | {published ? "✅ Published" : "📝 Draft"}
          </p>
        </div>
      )}

      {/* Blog list */}
      <h2 className="text-2xl font-bold mt-10 mb-3">📑 All Blogs</h2>

      {loadingBlogs ? (
        <p className="text-gray-500">Loading blogs...</p>
      ) : blogs.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2">Title</th>
                <th className="border p-2">Slug</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {blogs.map((b) => (
                <tr key={b.id}>
                  <td className="border p-2">{b.title}</td>
                  <td className="border p-2">{b.slug}</td>
                  <td className="border p-2 space-x-2">
                    <button
                      onClick={() => navigate(`/admin/blogs/editor?edit=${b.id}`)}
                      className="px-2 py-1 bg-yellow-500 text-white rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(b.id)}
                      className="px-2 py-1 bg-red-600 text-white rounded"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500">No blogs yet</p>
      )}
    </div>
  );
}
